import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { LoadError } from "@/components/LoadError";
import { publicName, publicYears } from "@/components/PublicPersonList";

const RelLink = ({ p }) => (
  <Link to={`/persons/${p.id}`} className="block heritage-card p-3 hover:bg-[#F0E6D8] transition-colors" data-testid={`public-rel-${p.id}`}>
    <div className="font-serif-heritage text-lg text-[#261911]">{publicName(p)}</div>
    <div className="text-xs text-[#6E5B4F]">{publicYears(p)}</div>
  </Link>
);

const RelGroup = ({ title, items, testid }) => (
  <div data-testid={testid}>
    <div className="label-uppercase text-[#6E5B4F] mb-2">{title}</div>
    {items.length ? <div className="space-y-2">{items.map(p => <RelLink key={p.id} p={p} />)}</div>
      : <p className="text-sm italic text-[#6E5B4F]">— Non renseigné —</p>}
  </div>
);

export const PublicPersonPage = () => {
  const { id } = useParams();
  const { API } = useAuth();
  const [p, setP] = useState(null);
  const [clans, setClans] = useState([]);
  const [error, setError] = useState(null);

  const load = async () => {
    setError(null);
    try {
      const [r, c] = await Promise.all([axios.get(`${API}/public/persons/${id}`, { timeout: 20000 }), axios.get(`${API}/clans`, { timeout: 20000 })]);
      setP(r.data); setClans(c.data);
    } catch (e) { setError(e.response?.status === 404 ? "private" : "network"); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  if (error === "network") return <LoadError onRetry={load} />;
  if (error === "private") return (
    <div className="max-w-xl mx-auto px-6 py-16 text-center" data-testid="public-person-private">
      <h1 className="font-serif-heritage text-3xl text-[#261911] mb-3">Fiche réservée aux membres</h1>
      <p className="text-[#6E5B4F] mb-6">Cette fiche n'est pas publique. Connectez-vous pour la consulter.</p>
      <Link to="/login" className="btn-terra text-sm">Se connecter</Link>
    </div>
  );
  if (!p) return <div className="p-10 text-center text-[#6E5B4F]" data-testid="public-person-loading">Chargement…</div>;
  const clan = clans.find(c => c.key === p.clan_key);

  return (
    <div className="max-w-4xl mx-auto px-6 sm:px-10 py-10" data-testid="public-person-page">
      {clan && <Link to={`/clans/${clan.key}`} className="text-sm text-[#993D22] flex items-center gap-1 mb-6" data-testid="public-back-clan"><ArrowLeft className="w-4 h-4" /> {clan.name}</Link>}
      <div className="heritage-card p-8 flex flex-col sm:flex-row gap-6 items-start">
        {p.has_photo ? (
          <img src={`${API}/public/persons/${p.id}/photo`} alt={publicName(p)} className="w-32 h-32 rounded-full object-cover" data-testid="public-person-photo" />
        ) : (
          <div className="w-32 h-32 rounded-full flex items-center justify-center text-[#FAF7F2] font-serif-heritage text-4xl shrink-0" style={{ background: clan?.color || "#993D22" }}>
            {`${p.last_name?.[0] || ""}${p.first_name?.[0] || ""}`}
          </div>
        )}
        <div className="flex-1">
          {clan && <div className="label-uppercase mb-1" style={{ color: clan.color }}>{clan.name}</div>}
          <h1 className="font-serif-heritage text-4xl sm:text-5xl text-[#261911]" data-testid="public-person-name">{publicName(p)}</h1>
          {p.alt_names?.length > 0 && <p className="italic text-[#6E5B4F] mt-1">Aussi appelé·e : {p.alt_names.join(", ")}</p>}
          {!p.living && (
            <div className="mt-4 flex flex-wrap gap-2 text-sm text-[#3A2312]" data-testid="public-person-dates">
              {p.birth_year && <span className="px-3 py-1 rounded-full bg-[#F0E6D8]">né·e {p.birth_year_approx ? "vers" : "en"} {p.birth_year}{p.birth_place ? ` · ${p.birth_place}` : ""}</span>}
              {!p.birth_year && p.birth_place && <span className="px-3 py-1 rounded-full bg-[#F0E6D8]">né·e à {p.birth_place}</span>}
              {p.death_year && <span className="px-3 py-1 rounded-full bg-[#F0E6D8]">décédé·e {p.death_year_approx ? "vers" : "en"} {p.death_year}</span>}
            </div>
          )}
          {p.living && <p className="text-xs text-[#6E5B4F] mt-3" data-testid="public-person-living-note">Personne vivante : seules son identité, sa famille et ses parents sont affichés publiquement.</p>}
          {!p.living && p.biography && (
            <div className="mt-6">
              <div className="label-uppercase text-[#6E5B4F] mb-2">Histoire orale</div>
              <p className="text-[#3A2312] whitespace-pre-line" data-testid="public-person-bio">{p.biography}</p>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-8">
        <RelGroup title="Père" items={p.father ? [p.father] : []} testid="public-father" />
        <RelGroup title="Mère" items={p.mother ? [p.mother] : []} testid="public-mother" />
        {!p.living && <RelGroup title="Conjoint·e·s" items={p.spouses || []} testid="public-spouses" />}
        {!p.living && <RelGroup title="Enfants" items={p.children || []} testid="public-children" />}
      </div>

      <div className="mt-10 heritage-card p-6 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="public-join-invite">
        <Users className="w-6 h-6 text-[#993D22] shrink-0" />
        <p className="flex-1 text-[#3A2312]">Vous connaissez cette personne ? Rejoignez Evondo Héritage pour contribuer à son histoire.</p>
        <Link to="/register" className="btn-terra text-sm whitespace-nowrap" data-testid="public-join-btn">Rejoindre</Link>
      </div>
    </div>
  );
};

export default PublicPersonPage;
